// 导入 Ant Design 组件
import { Drawer, Form, Input, InputNumber } from "antd"
// 导入 ethers 工具函数
import { formatEther, parseUnits } from "ethers";
// 导入 React hooks
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
// 导入图片资源
import card from '@/image/card.svg'
// 导入配置和常量
import { selectList, SYMBOL } from "@/collocate";
// 导入自定义组件
import ButtonPrimary from "@/components/ButtonPrimary";
import BalanceCom from "@/components/BalanceCom";
// 导入自定义 hooks
import useGetTokenBalance from "@/web3Hook/useGetTokenBalance";
import useGetAllowanceToken from "@/web3Hook/useGetAllowanceToken";
import dropdownpfeil from '@/image/dropdownpfeil.svg'
import closeSvg from '@/image/closeSvg.svg'
import useStake from "@/web3Hook/useStake";
import useGetUserInfo from "@/web3Hook/useGetUserInfo";
import useGetCurDayStakeLimit from "@/web3Hook/useGetCurDayStakeLimit";
import ScrollView from "@/components/ScrollView";
import LotteryWheel from "./LotteryWheel";
import useUpdateEffect from "@/hooks/useUpdateEffect";
import useCheckLotteryBuff2 from "@/web3Hook/useCheckLotteryBuff2";

// 定义表单字段类型
type FieldType = {
    pledge: number;      // 质押数量
    period: string;      // 质押周期
};

/**
 * RegularPledge - 定期质押组件
 * 
 * 用于用户进行定期质押操作，包括：
 * - 输入质押数量
 * - 选择质押周期
 * - 显示算力和倍数
 * - 授权和质押操作
 */
const RegularPledge = () => {
    // 国际化
    const { t, i18n: { language } } = useTranslation();
    // 表单实例
    const [form] = Form.useForm();
    // 是否拥有buff
    const { checkLotteryBuff2, isWinner } = useCheckLotteryBuff2();
    // 获取用户信息 
    const { userInfo, userInfoLod, getUserInfo } = useGetUserInfo();
    // Drawer 抽屉状态管理
    const [selectDrawerOpen, setSelectDrawerOpen] = useState(false);
    // 选中的质押周期
    const [period, setPeriod] = useState<number>();
    // 
    const { curDayStakeLimitLod, getCurDayStakeLimit, stakeDate } = useGetCurDayStakeLimit()
    // 关闭抽屉
    const close = () => {
        setSelectDrawerOpen(false);
    };

    // 打开抽屉
    const open = () => {
        setSelectDrawerOpen(true);
    }

    // 确认选择周期并更新表单
    const setFromPeriod = () => {
        if (!period) return
        form.setFieldsValue({ period: period + t('contract.days') });
        form.validateFields(['pledge']);
        close();
    }

    // 获取当前选中的周期
    const tempActive = Form.useWatch('period', form) || '';

    // 实时监听质押数量变化
    const pledgeNum = Form.useWatch('pledge', form) || 0;
    // 根据选中的周期获取对应的配置对象
    const tempActiveObj = useMemo(() => {
        const seleListNew = JSON.parse(JSON.stringify(selectList));
        const value = parseInt(tempActive)
        const obj = seleListNew.find((item: any) => item.value === value);
        // lotteryStatus: 2=BUFF_WIN
        if (isWinner && obj?.unlockCycle === seleListNew[seleListNew.length - 1].unlockCycle) {
            obj!.multiple = '5.0'
        }
        return obj;
    }, [isWinner, tempActive])

    // 获取授权相关方法和状态
    const { allowance, allowanceLod, approve, allowanceLodFun, getAllowance } = useGetAllowanceToken(1)
    // 获取代币余额相关方法和状态
    const { tokenBalance, getTokenBalance, tokenBalanceLod } = useGetTokenBalance()
    // 获取质押相关方法和状态
    const { stake, stakeLod } = useStake()


    // 判断是否已授权
    const isApprove = useMemo(() => {
        const pledgedPowerStr = pledgeNum ? pledgeNum + '' : '0';
        return allowance >= parseUnits(pledgedPowerStr);
    }, [allowance, pledgeNum]);

    // 表单提交处理
    const onFinish = (values: any) => {
        if (!isApprove) {
            // 未授权则先进行授权
            approve({
                success: t('contract.enableSuccess'),
                error: t('contract.enableFailed')
            })
        } else {
            // 已授权则执行质押
            stake(
                parseUnits(values.pledge + ''),
                tempActiveObj?.unlockCycle || 0,
                {
                    success: t('contract.stakingSuccess'),
                    error: t('contract.stakingFailed')
                },
                () => {
                    // 质押成功后刷新数据
                    getTokenBalance();
                    getAllowance()
                    form.resetFields();
                    getCurDayStakeLimit();
                    getUserInfo();
                    checkLotteryBuff2();
                },
                () => {
                    getCurDayStakeLimit()
                }
            )
        }
    };

    // 自定义校验规则：校验质押数量
    const mathematicalCheck = () => ({
        validator(_: any, value: number) {
            console.log('Number(formatEther(userInfo.totalStake))', Number(formatEther(userInfo.totalStake)));

            const isOver = Number(formatEther(userInfo.totalStake)) + Number(value) <= selectList[selectList.length - 1].max
            const isMin = Number(formatEther(stakeDate.pledgedDay)) + Number(value) <= Number(formatEther(stakeDate.stakeLimit))
            if (!value) {
                return Promise.resolve();
            } else if (tempActiveObj === undefined) {
                return Promise.resolve();
            } else if (value < tempActiveObj.min || value > tempActiveObj.max) {
                return Promise.reject(t('contract.stakingRangeIs', { period: tempActive, min: tempActiveObj.min, max: tempActiveObj.max }));
            } else if (value % 100 !== 0) {
                return Promise.reject(t('contract.mustBeMultipleOf100'));
            } else if (tokenBalance < parseUnits(value + '')) {
                return Promise.reject(t('contract.insufficientBalance'));
            } else if (!isOver) {
                return Promise.reject(t('contract.maxStakingPower', { max: tempActiveObj.max }));
            } else if (!isMin) {
                return Promise.reject(t('contract.exceedDailyLimit'));
            }
            else {
                return Promise.resolve();
            }
        },
    })
    useUpdateEffect(() => {
        setFromPeriod()
        form.submit()
    }, [language])
    return (
        <ScrollView className="mx-4 md:mx-6" px={"12.8125rem"}>
            <Form
                form={form}
                name="dsd1"
                initialValues={{ remember: true }}
                onFinish={onFinish}
                autoComplete="off"
            >
                <div className="bg-3c p-4 md:p-6 rounded-2xl">
                    {isWinner && <div className="text-white text-sm md:text-base mb-3">
                        {t('contract.lottery5xTip')}
                    </div>}
                    <div className="border rounded-lg border-a4 bg-49 p-3 md:p-4 pb-0">
                        <div className="text-white text-xs md:text-sm mb-2">
                            {t('contract.stakingAmount')}
                        </div>
                        <Form.Item<FieldType>
                            name="pledge"
                            rules={[
                                {
                                    required: true,
                                    message: t('contract.pleaseEnterStakingAmount')
                                },
                                mathematicalCheck(),
                            ]}
                        >
                            <InputNumber controls={false} suffix={SYMBOL} className="w-full" placeholder={t('contract.startWithMultiple', { num: selectList[0].min })} />
                        </Form.Item>
                    </div>
                    <div>
                        <div className="text-white text-xs md:text-sm mb-2 mt-4">
                            {t('contract.stakingPeriod')}
                        </div>
                        <Form.Item<FieldType>
                            name="period"
                            rules={[
                                {
                                    required: true,
                                    message: t('contract.pleaseSelectPeriod')
                                },
                            ]}
                        >
                            <Input placeholder={t('contract.pleaseSelect')} suffix={<img src={dropdownpfeil} alt="" />} readOnly className=" text-white  border-a4 bg-49 h-9.5 md:h-11 " onClick={open} />
                        </Form.Item>

                    </div>
                    <div className="flex items-center justify-between mt-5 text-white gap-3"  >
                        <div className="flex-1">
                            <div className="mb-2 text-xs md:text-sm">{t('contract.hashrate')}</div>
                            <div className="border border-a4 rounded-lg h-9.5 md:h-11 flex items-center justify-center font-semibold text-sm md:text-base">{tempActiveObj?.coreCount}</div>
                        </div>
                        <div className="flex-1">
                            <div className="mb-2 text-xs md:text-sm">{t('contract.multiple')}</div>
                            <div className="border border-a4 rounded-lg h-9.5 md:h-11 flex items-center justify-center font-semibold text-sm md:text-base">{tempActiveObj?.multiple}</div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-end mt-5">
                    <img src={card} className="w-6 h-6 mr-1.5" alt="" />
                    <BalanceCom qText={t('contract.balance') + "："} className="text-sm md:text-base text-d1" balance={tokenBalance} loading={tokenBalanceLod} />
                </div>
                {/* 提交按钮 */}
                <ButtonPrimary
                    className="h-11.25 md:h-12 mt-1.5"
                    htmlType="submit"
                    text={isApprove ? t('contract.confirm') : t('contract.enable')}
                    loading={tokenBalanceLod || allowanceLod || allowanceLodFun || stakeLod || userInfoLod || curDayStakeLimitLod}
                    block
                />
            </Form >

            <Drawer
                className="rounded-t-2xl bg-3e md:max-w-[600px] md:mx-auto"
                placement="bottom"
                open={selectDrawerOpen}
                onClose={() => setSelectDrawerOpen(false)}
                closeIcon={false}
                height={500}
            >
                <div className=" w-full">
                    <div className="flex justify-between items-center text-white text-base md:text-lg">
                        <span>{t('contract.stakingPeriod')}</span>
                        <img className=" w-6 h-6 " src={closeSvg} alt="" onClick={() => setSelectDrawerOpen(false)} />
                    </div>

                    <div className="mt-5 flex flex-col text-">
                        {selectList.map((item, index) => (
                            <div
                                className={`mb-3 border w-full h-14 md:h-16 flex items-center rounded-md  ${period === item.value ? "bg-38 border-fa text-white" : "text-white-80  bg-3a border-9e"
                                    }`}
                                key={index}
                                onClick={() => setPeriod(item.value)}
                            >
                                <span className="text-sm md:text-base ml-3">{item.label}{t('contract.days')}</span>
                            </div>
                        ))}
                    </div>
                    <ButtonPrimary
                        className="w-full h-11.5 md:h-12 text-sm md:text-base font-medium"
                        onClick={setFromPeriod}
                        text={t('contract.confirm')}
                    />

                </div>
            </Drawer >


            <div className="my-11">
                <LotteryWheel checkLotteryBuff2={checkLotteryBuff2} isWinner={isWinner} />
            </div>
        </ScrollView >
    )
}

export default RegularPledge